import { isMockApiFlag } from "../apiMode";
import { API_ENDPOINTS, apiService } from "../api";
import type { ApiVersion } from "../api";
import {
  mockCreateStory,
  mockDuplicateStory,
  mockGetStoryMeta,
  mockListStories,
  mockSetStoryPublishStatus,
  mockUpdateStoryMeta,
} from "./storiesApi.mock";
import type {
  CreateStoryRequest,
  CreateStoryResponse,
  ListStoriesQuery,
  StoriesListResponseApi,
  StoryMetaApi,
  UpdateStoryMetaRequest,
} from "./storiesApi.types";

const isMockStoriesApiEnabled = (): boolean =>
  isMockApiFlag(process.env.REACT_APP_STORIES_API);

/**
 * Its own flag, not REACT_APP_STORIES_API, so the story row routes can be
 * mocked on their own for offline work. Unset means live (api-service `dev`).
 */
const isMockStoryMetaApiEnabled = (): boolean =>
  isMockApiFlag(process.env.REACT_APP_STORY_META_API);

export const createStory = async (
  workspaceId: string,
  input: CreateStoryRequest,
  version?: ApiVersion,
): Promise<CreateStoryResponse> => {
  if (isMockStoriesApiEnabled()) return mockCreateStory(workspaceId, input);

  const formData = new FormData();
  if (input.prompt) formData.append("prompt", input.prompt);
  if (input.files) {
    input.files.forEach((file) => formData.append("files", file));
  }
  if (input.creation_method) {
    formData.append("creation_method", input.creation_method);
  }
  if (input.template_id) formData.append("template_id", input.template_id);
  if (input.title) formData.append("title", input.title);
  if (input.goal) formData.append("goal", input.goal);
  // Repeated entries rather than a JSON blob, matching how `files` is sent.
  input.audiences?.forEach((audience) =>
    formData.append("audiences", audience),
  );
  input.sources?.forEach((sourceId) => formData.append("sources", sourceId));
  if (input.data_model_id) {
    formData.append("data_model_id", input.data_model_id);
  }

  return apiService.post<CreateStoryResponse>(
    API_ENDPOINTS.stories.create(workspaceId, version),
    formData,
  );
};

/**
 * Params are omitted rather than defaulted when absent, so a bare call keeps
 * hitting the same URL the backend already defaults (limit=50, offset=0) — no
 * cache-busting query string for callers that do not page.
 */
const buildStoriesListUrl = (
  workspaceId: string,
  query?: ListStoriesQuery,
): string => {
  const base = API_ENDPOINTS.stories.list(workspaceId);
  const params = new URLSearchParams();
  if (query?.limit != null) params.set("limit", String(query.limit));
  if (query?.offset != null) params.set("offset", String(query.offset));
  if (query?.q) params.set("q", query.q);
  if (query?.exclude_story_id)
    params.set("exclude_story_id", query.exclude_story_id);
  const qs = params.toString();
  return qs ? `${base}?${qs}` : base;
};

export const listStories = async (
  workspaceId: string,
  query?: ListStoriesQuery,
): Promise<StoriesListResponseApi> => {
  if (isMockStoriesApiEnabled()) return mockListStories(workspaceId, query);
  return apiService.get<StoriesListResponseApi>(
    buildStoriesListUrl(workspaceId, query),
  );
};

/** Soft-deletes the story (owner only); the backend answers 204. */
export const deleteStory = async (
  workspaceId: string,
  storyId: string,
): Promise<void> => {
  if (isMockStoriesApiEnabled()) return;
  await apiService.delete<void>(API_ENDPOINTS.stories.remove(workspaceId, storyId));
};

// ─── Story row ────────────────────────────────────────────────────────────────
// All four return the full StoryResponse, so a caller can replace its cached
// copy with the result instead of refetching.

/**
 * With `poll`, `?poll=true` is appended so the backend skips the auto-refresh
 * side effect it runs on a plain story open. Carried over from the v1 GET:
 * without it the build page's 5s poll re-triggered the refresh workflow every
 * time the story came back to "ready", looping forever.
 */
export const getStoryMeta = async (
  workspaceId: string,
  storyId: string,
  poll?: boolean,
): Promise<StoryMetaApi> => {
  if (isMockStoryMetaApiEnabled())
    return mockGetStoryMeta(workspaceId, storyId, poll);
  const baseUrl = API_ENDPOINTS.stories.v2Meta(workspaceId, storyId);
  return apiService.get<StoryMetaApi>(poll ? `${baseUrl}?poll=true` : baseUrl);
};

export const updateStoryMeta = async (
  workspaceId: string,
  storyId: string,
  input: UpdateStoryMetaRequest,
): Promise<StoryMetaApi> => {
  if (isMockStoryMetaApiEnabled()) {
    return mockUpdateStoryMeta(workspaceId, storyId, input);
  }
  return apiService.patch<StoryMetaApi>(
    API_ENDPOINTS.stories.v2Meta(workspaceId, storyId),
    input,
  );
};

// Publish and unpublish take no body. `{}` is what apiService.post needs, as
// for the invite accept call.

export const publishStory = async (
  workspaceId: string,
  storyId: string,
): Promise<StoryMetaApi> => {
  if (isMockStoryMetaApiEnabled()) {
    return mockSetStoryPublishStatus(workspaceId, storyId, "published");
  }
  return apiService.post<StoryMetaApi>(
    API_ENDPOINTS.stories.v2Publish(workspaceId, storyId),
    {},
  );
};

export const unpublishStory = async (
  workspaceId: string,
  storyId: string,
): Promise<StoryMetaApi> => {
  if (isMockStoryMetaApiEnabled()) {
    return mockSetStoryPublishStatus(workspaceId, storyId, "draft");
  }
  return apiService.post<StoryMetaApi>(
    API_ENDPOINTS.stories.v2Unpublish(workspaceId, storyId),
    {},
  );
};

/** Copies a story into a new draft; resolves with the new story (201). */
export const duplicateStory = async (
  workspaceId: string,
  storyId: string,
): Promise<StoryMetaApi> => {
  if (isMockStoryMetaApiEnabled()) {
    return mockDuplicateStory(workspaceId, storyId);
  }
  return apiService.post<StoryMetaApi>(
    API_ENDPOINTS.stories.v2Duplicate(workspaceId, storyId),
    {},
  );
};
